import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { IoCheckmarkDoneSharp } from "react-icons/io5";

const MeetingForm = ({ closeModal, fetchMeetings }) => {
    const [attendiesOptions, setAttendiesOptions] = useState([]);
    const [availabilityMessage, setAvailabilityMessage] = useState(null);
    const [availabilityStatus, setAvailabilityStatus] = useState(false);
    const [isSlotAvailable, setIsSlotAvailable] = useState(false); // Controls the check button disable state
    const [selectedDates, setSelectedDates] = useState({ slot_start_time: null, slot_end_time: null });
    const meetingUrl = `${process.env.REACT_APP_BASE_URL}/meeting`;
    const availabilityUrl = `${process.env.REACT_APP_BASE_URL}/avl/meeting`;
    const checkAvailability = async (slot_start_time, slot_end_time) => {
        if (!slot_start_time || !slot_end_time) {
            setAvailabilityMessage("Please select start and end time.");
            return;
        }
        slot_start_time = format(new Date(slot_start_time), "yyyy-MM-dd HH:mm:ss");
        slot_end_time = format(new Date(slot_end_time), "yyyy-MM-dd HH:mm:ss");
        try {
            const response = await fetch(`${availabilityUrl}?slot_start_time=${encodeURIComponent(slot_start_time)}&slot_end_time=${encodeURIComponent(slot_end_time)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            const data = await response.json();
            console.log("API Response:", data);
            const formattedUsers = (data.data || [])
                .sort((a, b) => b.status - a.status) // Sort true (1) first, false (0) last
                .map(user => ({
                    value: user.user_id,
                    label: user.user_name || user.user_id,
                    ...user
                }));

            if (data?.status === true && data?.msg === "0") {
                Swal.fire({
                    title: "Slot Available",
                    icon: "success"
                });
                setIsSlotAvailable(true);
            } else if (data?.status === true && data?.msg === "1") {
                Swal.fire({
                    title: data.message,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    cancelButtonText: "No",
                    confirmButtonText: "Yes",
                }).then((result) => {
                    if (result.dismiss === Swal.DismissReason.cancel) {
                        closeModal(); // Call the closeModal function when "No" is clicked
                    }
                });
            } else {
                Swal.fire({
                    title: data?.message || "Slot Not Available",
                    icon: "error"
                });
                setIsSlotAvailable(false);
            }
            setAttendiesOptions(formattedUsers);
            setAvailabilityStatus(data.msg);
        } catch (error) {
            console.error("API Call Error:", error);
            setIsSlotAvailable(false);
        }
    };

    // Reset check button when user selects a new date
    useEffect(() => {
        setIsSlotAvailable(false);
    }, [selectedDates.slot_start_time, selectedDates.slot_end_time]);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Title is required"),
        description: Yup.string().required("Description is required"),
        slot_start_time: Yup.string().required("Start time is required"),
        slot_end_time: Yup.string().required("End time is required"),
        attendies: Yup.array().min(1, "At least one attendee is required"),
    });

    return (
        <div className="modal-overlay">
            <div className="modal-container animate-modal">
                <h2 className="modal-title">📅 New Meeting</h2>
                <Formik
                    initialValues={{
                        title: "",
                        description: "",
                        slot_start_time: "",
                        slot_end_time: "",
                        attendies: [],
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                        const token = localStorage.getItem("token");
                        const formattedValues = {
                            ...values,
                            slot_start_time: format(new Date(values.slot_start_time), "yyyy-MM-dd HH:mm:ss"),
                            slot_end_time: format(new Date(values.slot_end_time), "yyyy-MM-dd HH:mm:ss"),
                            attendies: values.attendies,
                        };

                        fetch(meetingUrl, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify(formattedValues),
                        })
                            .then(response => response.json())
                            .then(data => {
                                Swal.fire({
                                    title: "Meeting Created!",
                                    text: "Your meeting has been successfully scheduled.",
                                    confirmButtonText: "OK",
                                });

                                resetForm();
                                closeModal();
                                fetchMeetings();
                            })
                            .catch(error => {
                                console.error("Error creating meeting:", error);
                                Swal.fire({
                                    title: "Error",
                                    text: "Failed to create the meeting. Please try again.",
                                    confirmButtonText: "OK",
                                });
                            })
                            .finally(() => {
                                setSubmitting(false);
                            });
                    }}
                >
                    {({ handleSubmit, setFieldValue, values }) => (
                        <Form onSubmit={handleSubmit}>
                            <div className="date-time-container">
                                <div>
                                    <DatePicker
                                        selected={values.slot_start_time}
                                        onChange={(date) => {
                                            setFieldValue("slot_start_time", date);
                                            setSelectedDates(prev => ({ ...prev, slot_start_time: date }));
                                        }}
                                        showTimeSelect
                                        dateFormat="Pp"
                                        className="input-field"
                                        placeholderText="Start Time"
                                        minDate={new Date()}
                                    />
                                    <ErrorMessage name="slot_start_time" component="div" className="error-message" />
                                </div>
                                <span className="arrow">➡</span>
                                <div>
                                    <DatePicker
                                        selected={values.slot_end_time}
                                        onChange={(date) => {
                                            setFieldValue("slot_end_time", date);
                                            setSelectedDates(prev => ({ ...prev, slot_end_time: date }));
                                        }}
                                        showTimeSelect
                                        dateFormat="Pp"
                                        className="input-field"
                                        placeholderText="End Time"
                                        minDate={values.slot_start_time || new Date()}
                                    />
                                    <ErrorMessage name="slot_end_time" component="div" className="error-message" />
                                </div>
                                <button
                                    type="button"
                                    className="search-button-time-container"
                                    onClick={() => checkAvailability(values.slot_start_time, values.slot_end_time)}
                                    disabled={
                                        !(selectedDates.slot_start_time && selectedDates.slot_end_time) ||  // No date selected
                                        isSlotAvailable ||  // Slot already available
                                        values.attendies.length > 0  // Disable if attendees are selected
                                    }
                                    style={{
                                        backgroundColor:
                                            !(selectedDates.slot_start_time && selectedDates.slot_end_time) ||
                                                isSlotAvailable ||
                                                values.attendies.length > 0
                                                ? "gray"
                                                : "#04a5e8",
                                        color: "white",
                                        cursor:
                                            !(selectedDates.slot_start_time && selectedDates.slot_end_time) ||
                                                isSlotAvailable ||
                                                values.attendies.length > 0
                                                ? "not-allowed"
                                                : "pointer",

                                        opacity:
                                            !(selectedDates.slot_start_time && selectedDates.slot_end_time) ||
                                                isSlotAvailable ||
                                                values.attendies.length > 0
                                                ? 0.6
                                                : 1,
                                    }}
                                >
                                    {isSlotAvailable ? (
                                        <IoCheckmarkDoneSharp size={20} />
                                    ) : (
                                        "Check"
                                    )}
                                </button>


                            </div>

                            {availabilityMessage && (
                                <div className="availability-message">{availabilityMessage}</div>
                            )}

                            <Field type="text" name="title" className="input-field" placeholder="📌 Add title" />
                            <ErrorMessage name="title" component="div" className="error-message" />

                            {availabilityStatus && (
                                <Select
                                    name="attendies"
                                    className="input-field custom-select"
                                    options={attendiesOptions}
                                    isMulti
                                    value={attendiesOptions && attendiesOptions.filter(option =>
                                        values.attendies.some(attendee => attendee.user_id === option.value)
                                    )}
                                    onChange={selectedOptions =>
                                        setFieldValue("attendies", selectedOptions ? selectedOptions.map(option => ({
                                            user_id: option.value,
                                            user_name: option.label,
                                            designation: option.designation
                                        })) : [])
                                    }
                                    getOptionLabel={(e) => (
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                whiteSpace: "nowrap",  // Prevent text from wrapping
                                                overflow: "hidden",    // Hide overflowed text
                                                textOverflow: "ellipsis",  // Add "..." for overflowed text
                                                maxWidth: "100%",   // Ensure it doesn't exceed container width
                                                color: e.status ? "black" : "darkred",
                                                // backgroundColor: e.status ? "#fff" : "crimson",
                                                fontWeight: "600",
                                                opacity: e.status ? 1 : 0.5,
                                                cursor: e.status ? "pointer" : "not-allowed",
                                                textTransform: "capitalize"
                                            }}
                                            title={!e.status ? "Already booked" : ""}
                                        >
                                            {e.label}
                                        </div>
                                    )}
                                    isOptionDisabled={(option) => !option.status}  // Disable red status options
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            minHeight: "30px", // Adjust height of select input box
                                            fontSize: "14px",
                                            // overflow: "hidden"
                                        }),
                                        dropdownIndicator: (provided) => ({
                                            ...provided,
                                            padding: "4px"
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            padding: "5px 10px", // Reduce padding for smaller height
                                            fontSize: "14px",
                                            minHeight: "30px",
                                            backgroundColor: state.isFocused ? "#f0f0f0" : "white",
                                            color: "#333",
                                            whiteSpace: "nowrap", // Ensure text doesn't wrap
                                            // overflow: "hidden",
                                            overflowY: "auto",
                                            textOverflow: "ellipsis",
                                            maxWidth: "100%"
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            width: "200px",
                                            overflowY: "auto"
                                        })
                                    }}
                                />
                            )}
                            <Field as="textarea" name="description" className="textarea-field" placeholder="📝 Meeting description" />
                            <ErrorMessage name="description" component="div" className="error-message" />

                            <div className="button-group">
                                <button type="button" className="close-button" onClick={closeModal}>
                                    Close
                                </button>
                                <button type="submit" className="save-button">
                                    Save
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default MeetingForm;
